/**
 * src/lib/inflightCvRecovery.js — RC 1.49.x P0 (operator-locked, 2026-05-07)
 *
 * Lightweight, frontend-only recovery for the "navigate-away during
 * Generate CV" UX gap.
 *
 * Audit context (read-only audit, 2026-05-07):
 *   • Generate-CV flow is `POST /api/cv-variants/generate` →
 *     `POST /api/cv-variants/{id}/finalize-charge`. The variant is
 *     ALWAYS persisted before any charge fires. There is no silent
 *     overcharge.
 *   • If the wizard component unmounts mid-flight, `setGeneratedResult`
 *     never runs — the user is then forced to recover the variant
 *     manually from Documents → Variants.
 *
 * Strategy (operator-locked):
 *   • At generation start, write a sessionStorage marker keyed by
 *     `(userId, jobId)` carrying the deterministic title stamp the
 *     wizard already mints (`Variant for {title} — {company} —
 *     {YYYYMMDDHHMMSS}-{shortJobSuffix}{variantSuffix}`).
 *   • On wizard remount, if the marker exists AND the restored
 *     wizard state has no `generatedResult`, GET `/api/cv-variants`
 *     and look for an EXACT title-stamp match. On match, return the
 *     row so the wizard can rehydrate `generatedResult` /
 *     `selectedCv` and clear the marker.
 *   • If no match within RECOVERY_WINDOW_MS, the marker is cleared
 *     and the caller shows a calm toast directing the user to
 *     Documents → Variants.
 *
 * Hard rails honoured:
 *   • No new endpoint. No schema change. No billing change.
 *   • No "latest variant" heuristic — exact-title match only.
 *   • Cleared on success so a retry never reuses a stale marker.
 */

import { apiFetch } from './api';

// Slot just over the longest realistic LLM call (which routinely
// runs 30-60s) plus a small slack for finalize-charge.
//
// RC 1.49.4 L-5 (operator-locked, 2026-05-08) — widened from 5 min
// to 10 min so the recovery window matches the
// `IDEMPOTENCY_TTL_MS` in `idempotencyKeys.js`. Mobile-suspend
// scenarios (background → phone call → return ~7 min later) now
// have a single coherent recovery budget across BOTH the in-flight
// marker AND the financial idempotency key.
export const INFLIGHT_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

const KEY_PREFIX = 'inflight-cv';

function _key(userId, jobId) {
  return `${KEY_PREFIX}:${userId || ''}:${jobId || ''}`;
}

/** Write the in-flight marker. Idempotent. Silent on private-mode.
 *
 * RC 1.51 follow-up B1 (operator-locked, 2026-02) — the marker now
 * carries the durable `variant_id` returned by the pending-row
 * INSERT on the backend. When present, recovery is deterministic:
 * `GET /api/cv-variants/{variant_id}` returns the canonical row
 * (status `queued | running | complete | failed`) — no title scan.
 * `title` is retained as a back-compat heuristic for any pre-B1
 * marker still sitting in sessionStorage from before this rollout.
 */
export function writeInflightCvMarker(userId, jobId, title, variantId = null) {
  if (!jobId || (!title && !variantId)) return;
  try {
    sessionStorage.setItem(
      _key(userId, jobId),
      JSON.stringify({
        submittedAtMs: Date.now(),
        title:         title || null,
        variantId:     variantId || null,
        startedAt:     new Date().toISOString(),
      }),
    );
  } catch { /* sessionStorage unavailable — non-fatal */ }
}

/** Drop the marker. Always safe to call. */
export function clearInflightCvMarker(userId, jobId) {
  if (!jobId) return;
  try {
    sessionStorage.removeItem(_key(userId, jobId));
  } catch { /* no-op */ }
}

/** Read the marker. Returns the parsed payload or null.
 *
 * RC 1.51 follow-up B1 (operator-locked, 2026-02) — accepts either
 *   • a B1 marker with `variantId` (preferred — deterministic
 *     id-driven recovery), or
 *   • a pre-B1 marker with only `title` (back-compat — title-stamp
 *     heuristic still works during the rollout window).
 */
export function readInflightCvMarker(userId, jobId) {
  if (!jobId) return null;
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId));
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (!payload || typeof payload !== 'object') return null;
    if (!payload.submittedAtMs) return null;
    // Either a variantId or a title must be present to drive recovery.
    if (!payload.variantId && !payload.title) return null;
    return payload;
  } catch { return null; }
}

/** True when the marker is older than the recovery window. */
export function isInflightMarkerExpired(marker, nowMs = Date.now()) {
  if (!marker?.submittedAtMs) return true;
  return (nowMs - Number(marker.submittedAtMs)) > INFLIGHT_RECOVERY_WINDOW_MS;
}

/**
 * Look for a `cv_variants` row whose title EXACTLY matches the
 * marker's stamped title. Deterministic — no "freshest row" guess.
 *
 * @returns {Promise<object|null>} the matching variant row, or null.
 */
export async function findVariantByTitleStamp(title) {
  if (!title) return null;
  try {
    const rows = await apiFetch('/api/cv-variants');
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows.find((r) => r?.title === title || r?.variant_name === title) || null;
  } catch { return null; }
}
